import { useTable } from 'common/components/DataTable/hooks';
import usePaginationSearchParams from 'common/hooks/usePaginationSearchParams';
import { Pagination } from 'common/types/Pagination';
import { HeadCell } from 'common/types/MuiComponents';

import React, { useEffect } from 'react';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

interface Props {
  pagination: Pagination | undefined;
  headCells: HeadCell[];
  renderRows: () => JSX.Element[];
  searchValue?: string;
  updateDataCallback: (abortController: AbortController) => void;
}

const DataTable: React.FC<Props> = ({ pagination, headCells, renderRows, searchValue, updateDataCallback }) => {
  const { pageParam, rowsParam, setPageParam } = usePaginationSearchParams();
  const { handleChangePage, handleChangeRowsPerPage } = useTable(searchValue);

  useEffect(() => {
    const abortController = new AbortController();
    updateDataCallback(abortController);

    return () => {
      abortController.abort();
    };
  }, [ updateDataCallback ]);

  useEffect(() => {
    if (pagination && pagination.pageCount !== 0 && Number(pageParam) > pagination.pageCount) {
      setPageParam('1');
    }
  }, [ pagination, pageParam, setPageParam ]);

  return (
    <>
      <TableContainer data-testid="data-table">
        <Table sx={{ minWidth: 750 }}>
          <TableHead>
            <TableRow>
              {headCells.map(headCell => (
                <TableCell key={headCell.id}>
                  <Stack direction="row" justifyContent={headCell.numeric ? 'flex-end' : 'flex-start'} alignItems="center">
                    {headCell.label}
                    {headCell.button}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {renderRows()}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination
          rowsPerPageOptions={[ 10, 25 ]}
          component="div"
          count={pagination.rowCount}
          labelRowsPerPage="Rows per page"
          rowsPerPage={Number(rowsParam)}
          page={Number(pageParam) <= pagination.pageCount ? Number(pageParam) - 1 : 0}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          showFirstButton
          showLastButton
        />
      )}
    </>
  );
};

export default DataTable;
